import React from "react";
import PropTypes from "prop-types";
import { Avatar, Box, Typography } from "@mui/material";
import { Star } from "@mui/icons-material";
import { alpha } from "@mui/material/styles";
import { colors } from "../../theme/colors";
import EloBadge from "../common/EloBadge.jsx";
import TooltipComponent from "../common/Tooltip";
import { FOUNDER_BADGE, FOUNDER_TOOLTIP_TEXT } from "../../constants/founder";

const ROW_SX = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: "0.7rem",
    minHeight: "3.15rem",
    padding: "0.5rem 0.65rem 0.5rem 0.7rem",
    boxSizing: "border-box",
    borderRadius: "12px",
    position: "relative",
    overflow: "hidden",
    background: `
        linear-gradient(
            180deg,
            rgba(24, 26, 31, 0.96) 0%,
            rgba(14, 16, 20, 0.98) 100%
        )
    `,
    border: `1px solid ${colors.border.default}`,
    boxShadow: `
        0 8px 24px rgba(0,0,0,0.35),
        inset 0 1px 0 rgba(255,255,255,0.04)
    `,
};

const OFFLINE_PILL_SX = {
    flexShrink: 0,
    display: "inline-flex",
    alignItems: "center",
    px: "0.42rem",
    py: "0.2rem",
    borderRadius: "999px",
    border: "1px solid rgba(255,255,255,0.065)",
    background:
        "linear-gradient(100deg, rgba(255,255,255,0.032), rgba(255,255,255,0.014))",
    boxShadow:
        "inset 0 1px 0 rgba(255,255,255,0.025), 0 4px 12px rgba(0,0,0,0.12)",
};

const OFFLINE_PILL_TEXT_SX = {
    fontSize: "0.52rem",
    fontWeight: 700,
    lineHeight: 1,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    color: alpha(colors.text.primary, 0.56),
    whiteSpace: "nowrap",
};

const LobbyPlayerMobileRow = React.forwardRef(function LobbyPlayerMobileRow(
    {
        player,
        isYou = false,
        isLobbyHost = false,
        appearsDisconnected = false,
        showSearchingLayer = false,
        avatarDisplaySrc,
        onAvatarError,
        founderBadge = null,
        previewOpen = false,
        onOpenPreview,
    },
    ref,
) {
    const nickname = player?.nickname || "Player";
    const canOpenPreview = typeof onOpenPreview === "function";

    return (
        <Box
            ref={ref}
            onClick={canOpenPreview ? onOpenPreview : undefined}
            onKeyDown={
                canOpenPreview
                    ? (event) => {
                          if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              onOpenPreview();
                          }
                      }
                    : undefined
            }
            tabIndex={canOpenPreview ? 0 : undefined}
            role={canOpenPreview ? "button" : undefined}
            aria-haspopup={canOpenPreview ? "dialog" : undefined}
            aria-expanded={canOpenPreview ? previewOpen : undefined}
            aria-label={
                canOpenPreview
                    ? `Open ${nickname} player card`
                    : undefined
            }
            sx={{
                ...ROW_SX,
                cursor: canOpenPreview ? "pointer" : "default",
                outline: "none",
                filter: appearsDisconnected
                    ? "grayscale(0.55) brightness(0.78)"
                    : "none",
                opacity: appearsDisconnected ? 0.88 : 1,
                ...(isYou && {
                    borderColor: alpha(colors.primary.main, 0.28),
                    boxShadow: `
                        0 8px 24px rgba(0,0,0,0.35),
                        0 0 0 1px ${alpha(colors.primary.main, 0.08)},
                        inset 0 1px 0 rgba(255,255,255,0.05)
                    `,
                }),
                ...(canOpenPreview && {
                    "&:hover, &:focus-visible": {
                        borderColor: alpha(colors.primary.main, 0.28),
                    },
                }),
            }}
        >
            {showSearchingLayer ? (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: alpha(colors.background.card, 0.4),
                        zIndex: 2,
                        pointerEvents: "none",
                    }}
                />
            ) : null}

            <Box
                sx={{
                    position: "relative",
                    width: "2.55rem",
                    height: "2.55rem",
                    flexShrink: 0,
                }}
            >
                <Avatar
                    src={avatarDisplaySrc}
                    onError={onAvatarError}
                    sx={{
                        width: "100%",
                        height: "100%",
                        border: `1px solid ${alpha("#fff", 0.1)}`,
                        backgroundColor: alpha("#fff", 0.06),
                    }}
                />
                {player?.flagImageUrl && !appearsDisconnected ? (
                    <Box
                        component="img"
                        src={player.flagImageUrl}
                        alt=""
                        aria-hidden="true"
                        sx={{
                            position: "absolute",
                            right: "-2px",
                            bottom: "-2px",
                            width: "0.85rem",
                            height: "0.85rem",
                            borderRadius: "50%",
                            border: `1.5px solid ${alpha(colors.background.surface, 0.7)}`,
                            objectFit: "cover",
                        }}
                    />
                ) : null}
                {founderBadge ? (
                    <TooltipComponent title={FOUNDER_TOOLTIP_TEXT} placement="top" arrow>
                        <Box
                            sx={{
                                position: "absolute",
                                top: "-4px",
                                left: "-4px",
                                display: "flex",
                                alignItems: "center",
                                gap: "0.08rem",
                                backgroundColor: "rgba(28, 30, 32, 0.92)",
                                border: `1px solid ${FOUNDER_BADGE.borderColor}`,
                                borderRadius: "6px",
                                padding: "0.02rem 0.2rem",
                                zIndex: 1,
                            }}
                        >
                            <Star
                                sx={{
                                    color: FOUNDER_BADGE.color,
                                    fontSize: "0.55rem",
                                }}
                            />
                            <Typography
                                component="span"
                                sx={{
                                    color: FOUNDER_BADGE.color,
                                    fontSize: "0.42rem",
                                    fontWeight: 700,
                                    lineHeight: 1,
                                }}
                            >
                                #{founderBadge.number || ""}
                            </Typography>
                        </Box>
                    </TooltipComponent>
                ) : null}
            </Box>

            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "0.55rem",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.35rem",
                        minWidth: 0,
                    }}
                >
                    {isLobbyHost ? (
                        <TooltipComponent title="Leader">
                            <Star
                                sx={{
                                    color: colors.primary.main,
                                    fontSize: "1rem",
                                    flexShrink: 0,
                                }}
                            />
                        </TooltipComponent>
                    ) : null}
                    <Typography
                        title={nickname}
                        sx={{
                            color: colors.text.primary,
                            fontSize: "0.92rem",
                            fontWeight: 700,
                            letterSpacing: "0.02em",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            minWidth: 0,
                            lineHeight: 1.2,
                        }}
                    >
                        {nickname}
                    </Typography>
                    {appearsDisconnected ? (
                        <Box sx={OFFLINE_PILL_SX}>
                            <Typography sx={OFFLINE_PILL_TEXT_SX}>
                                Offline
                            </Typography>
                        </Box>
                    ) : null}
                </Box>
                <Box sx={{ flexShrink: 0 }}>
                    <EloBadge value={player?.elo} size="small" />
                </Box>
            </Box>

        </Box>
    );
});

LobbyPlayerMobileRow.propTypes = {
    player: PropTypes.object,
    isYou: PropTypes.bool,
    isLobbyHost: PropTypes.bool,
    appearsDisconnected: PropTypes.bool,
    showSearchingLayer: PropTypes.bool,
    avatarDisplaySrc: PropTypes.string,
    onAvatarError: PropTypes.func,
    founderBadge: PropTypes.object,
    previewOpen: PropTypes.bool,
    onOpenPreview: PropTypes.func,
};

export default LobbyPlayerMobileRow;
